import { PageBackgroundData } from 'types/pageTypes';
import { getColorAtCorner } from 'utils/gradient';

import { LEFT_OFFSET, TOP_OFFSET } from './PageNumber.config';

/**
 * Get the page colour to use when computing the page number colour
 *
 * @param pageBackgroundData Page background property for page
 * @returns Color of background to use when computing page number colour
 */
const getPageColor = (pageBackgroundData: PageBackgroundData) => {
  const { background: solidColor, gradientBackground: gradient } = pageBackgroundData;

  // Solid color with no defined gradient, just return the colour
  if (!gradient) return solidColor || '';

  // Gradient, return colour at bottom right corner
  return getColorAtCorner(gradient);
};

/**
 * Calculate the position of the page number based on the font size, font family and page number
 * This function is to keep the page number position in the bottom right corner of the page when the font size is changed
 *
 * @param pageNumber
 * @param fontFamily
 * @param fontSize
 * @returns Position of the page number - { top, left }
 */
const getPageNumberTextDimensions = (pageNumber: number, fontFamily: string, fontSize: number) => {
  const pageNumberText = document.createElement('span');
  document.body.appendChild(pageNumberText);

  // set text for page number test element to be not visible and out of visibility range
  pageNumberText.style.visibility = 'hidden';
  pageNumberText.style.pointerEvents = 'none';
  pageNumberText.style.top = '-9999px';
  pageNumberText.style.left = '-9999px';

  // set the font properties
  pageNumberText.style.font = fontFamily;
  pageNumberText.style.fontSize = fontSize + 'px';
  pageNumberText.style.height = 'auto';
  pageNumberText.style.width = 'auto';
  pageNumberText.style.position = 'absolute';
  pageNumberText.style.whiteSpace = 'nowrap';
  pageNumberText.innerHTML = pageNumber.toString();

  // get the width and height of the page number text element
  const width = pageNumberText.clientWidth;
  const height = pageNumberText.clientHeight;

  // clean-up pageNumberText used for measuring
  document.body.removeChild(pageNumberText);

  return { width, height };
};

/**
 * Calculate the position of the page number based on the font size
 *
 * @param pageNumber
 * @param fontFamily
 * @param fontSize
 * @returns Position of the page number - { top, left }
 */
const calculatePageNumberPosition = (pageNumber: number, fontFamily: string, fontSize: number) => {
  const { width, height } = getPageNumberTextDimensions(pageNumber, fontFamily, fontSize);

  // calculate position for page number from bottom right using top and left offsets
  const top = `calc(100% - ${height}px - ${TOP_OFFSET}px)`;
  const left = `calc(100% - ${width}px - ${LEFT_OFFSET}px)`;

  return {
    top,
    left,
  };
};

export { calculatePageNumberPosition, getPageColor };
